import { useEffect, useRef, useState } from 'react';

import {
  realtimeClient,
  type RealtimeEvent,
  type RealtimeStatus,
} from '@/lib/realtime/realtimeClient';

export type { RealtimeEvent, RealtimeStatus } from '@/lib/realtime/realtimeClient';

export interface UseRealtimeStreamOptions {
  topics: string[];
  onEvent: (event: RealtimeEvent) => void;
  onStatus?: (status: RealtimeStatus) => void;
}

/**
 * Hook bas niveau : abonne le composant au client realtime PARTAGÉ (une seule
 * connexion EventSource par onglet, cf. realtimeClient).
 *
 *   useRealtimeStream({
 *     topics: ['orders', 'notifications'],
 *     onEvent(event) { ... },
 *   })
 *
 * `onEvent` reçoit chaque événement des topics demandés, plus un événement
 * synthétique `{ type: 'sync' }` par topic à la (re)connexion et en fallback
 * (pour déclencher un refetch). Reconnexion, backoff, heartbeat, Last-Event-ID
 * et fallback sont gérés par le client partagé ; ce hook ne fait que
 * s'abonner/se désabonner (nettoyage au démontage, pas de fuite).
 */
export function useRealtimeStream({ topics, onEvent, onStatus }: UseRealtimeStreamOptions): RealtimeStatus {
  const onEventRef = useRef(onEvent);
  onEventRef.current = onEvent;
  const onStatusRef = useRef(onStatus);
  onStatusRef.current = onStatus;

  const [status, setStatus] = useState<RealtimeStatus>('connecting');

  // Clé stable : ne se réabonne que si la LISTE des topics change réellement.
  const topicsKey = [...topics].sort().join(',');

  useEffect(() => {
    const unsubscribe = realtimeClient.subscribe({
      topics: topicsKey.split(',').filter(Boolean),
      onEvent: (event) => onEventRef.current(event),
      onStatus: (s) => {
        setStatus(s);
        onStatusRef.current?.(s);
      },
    });
    return unsubscribe;
  }, [topicsKey]);

  return status;
}
